/**
 * Payment-method gate for enabling deferred platform-fee billing.
 *
 * Mirrors the deerlake "Custom Payment" install dialog (step 2): it lists the
 * cards on file as a radio group (auto-selecting the first), lets the user add a
 * new card, and only allows confirming once a payment method has been *selected*.
 * Confirming sets the chosen card as the customer's default (so deferred invoices
 * bill it) and then calls `onConfirm`. We never enable deferred billing until a
 * payment method exists and is selected.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { skipToken } from '@reduxjs/toolkit/query';

import { Modal } from '@/components/Modal/Modal';
import { Column, Row } from '@/components/SettingsLayout';
import { AddPaymentMethodModal } from '@/components/StripePaymentMethod/AddPaymentMethodModal';
import { useGetPaymentMethodListQuery } from '@/store/slices/stripeApiSlice';
import { Button, Text } from '@/ui';
import RadioInput from '@/ui/RadioInput/RadioInput';
import { updateDefaultPaymentMethod } from '@/utils/helpers/stripeServices';

type PaymentMethodCard = {
  id: string;
  card?: {
    brand?: string;
    last4?: string;
    exp_month?: number;
    exp_year?: number;
  };
};

const CARD_BRANDS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

// Display label for a card brand (e.g. "visa" -> "Visa"); falls back to capitalised.
const formatCardBrand = (brand?: string) => {
  if (!brand) return 'Card';
  return CARD_BRANDS[brand.toLowerCase()] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
};

const formatCardLabel = (pm: PaymentMethodCard) => {
  const brand = formatCardBrand(pm.card?.brand);
  const last4 = pm.card?.last4 ?? '';
  const exp =
    pm.card?.exp_month && pm.card?.exp_year ? ` · ${pm.card.exp_month}/${pm.card.exp_year}` : '';
  return `${brand} •••• ${last4}${exp}`;
};

type Props = {
  open: boolean;
  companyId: string;
  stripeCustomerId: string;
  /** Parent's enable mutation is in flight. */
  isConfirming: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
};

function DeferredFeesPaymentModal({
  open,
  companyId,
  stripeCustomerId,
  isConfirming,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);

  const {
    data: paymentMethodList,
    isFetching,
    refetch,
  } = useGetPaymentMethodListQuery(
    open && stripeCustomerId ? { companyId, customerId: stripeCustomerId } : skipToken,
  );
  const paymentMethods: PaymentMethodCard[] = useMemo(
    () => paymentMethodList?.data ?? [],
    [paymentMethodList],
  );

  // Reset the selection whenever the modal closes.
  useEffect(() => {
    if (!open) {
      setSelectedMethodId(null);
    }
  }, [open]);

  // Auto-select the first card on file (matches the custom-payment flow).
  useEffect(() => {
    if (paymentMethods.length > 0 && !selectedMethodId) {
      setSelectedMethodId(paymentMethods[0].id);
    }
  }, [paymentMethods, selectedMethodId]);

  const busy = isConfirming || settingDefault;

  const handleConfirm = async () => {
    if (!selectedMethodId) return;
    setSettingDefault(true);
    try {
      // Make the selected card the default so deferred invoices bill it.
      await updateDefaultPaymentMethod(companyId, stripeCustomerId, selectedMethodId);
      await onConfirm();
    } catch {
      toast.error(t('Companies.deferred_fees_set_default_error'));
    } finally {
      setSettingDefault(false);
    }
  };

  return (
    <>
      <Modal open={open} closeModal={onCancel} closeOnOverlayClick={false}>
        <Modal.Title title={t('Companies.deferred_fees_select_title')} />
        <Modal.Content>
          <Column gapSize='16'>
            <Text size='s' weight='light' color='base-grey'>
              {t('Companies.deferred_fees_select_comment')}
            </Text>

            {isFetching ? (
              <Text size='s' weight='light' color='base-grey'>
                {t('Companies.deferred_fees_loading_cards')}
              </Text>
            ) : paymentMethods.length === 0 ? (
              <Column gapSize='8'>
                <Text size='s' weight='light' color='base-grey'>
                  {t('Companies.deferred_fees_no_cards')}
                </Text>
                <Button variant='outline' type='button' onClick={() => setAddCardOpen(true)}>
                  {t('Companies.deferred_fees_add_card')}
                </Button>
              </Column>
            ) : (
              <Column gapSize='8'>
                {paymentMethods.map((pm) => (
                  <RadioInput
                    key={pm.id}
                    id={`deferred-pm-${pm.id}`}
                    checked={selectedMethodId === pm.id}
                    onChange={() => setSelectedMethodId(pm.id)}
                    label={formatCardLabel(pm)}
                    disabled={busy}
                    testId={`deferred-pm-${pm.id}`}
                  />
                ))}
                <Button
                  variant='transparent'
                  type='button'
                  onClick={() => setAddCardOpen(true)}
                  disabled={busy}
                >
                  {t('Companies.deferred_fees_add_new_card')}
                </Button>
              </Column>
            )}
          </Column>
        </Modal.Content>
        <Modal.Footer justify='flex-end'>
          <Row gapSize='8'>
            <Button variant='outline' type='button' onClick={onCancel} disabled={busy}>
              {t('Shared.cancel')}
            </Button>
            <Button
              variant='primary'
              type='button'
              onClick={handleConfirm}
              disabled={busy || !selectedMethodId}
              testId='deferred-fees-confirm'
            >
              {t('Companies.deferred_fees_enable')}
            </Button>
          </Row>
        </Modal.Footer>
      </Modal>

      <AddPaymentMethodModal
        open={addCardOpen}
        companyId={companyId}
        stripeCustomerId={stripeCustomerId}
        onSuccess={(paymentMethodId) => {
          // AddPaymentMethodModal already set the new card as default; select it so
          // confirming bills the right card (don't fall back to auto-selecting the
          // first in the refetched list, whose ordering isn't guaranteed).
          setAddCardOpen(false);
          setSelectedMethodId(paymentMethodId);
          refetch();
        }}
        onCancel={() => setAddCardOpen(false)}
      />
    </>
  );
}

export default DeferredFeesPaymentModal;
